import axios, { AxiosError } from 'axios'
import {
    useQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query'
import { useDoseAddUndo as useDoseAddUndo } from '@/state/doses'

import type { Dose, DoseFormInput } from '../types'
import { ESTATE_IS_READY_QUERY_KEY } from './use-estate'

export function useDoses(appliancePlanId) {
    return useQuery< Array<Dose>, AxiosError >({
        queryKey: ['doses-' + appliancePlanId],
        queryFn: async () => {
            const { data } : { data : Array<Dose> } = await axios.get(
                '/api/dose_time_frame/list?appliance_plan_id=' + appliancePlanId,
            )
            return data
        },
    })
}

export function useDose(doseId) {
    return useQuery({
        queryKey: ['dose-' + doseId],
        queryFn: async () => {
            const { data } : { data : Dose } = await axios.get(
                '/api/dose_time_frame/single?id=' + doseId,
            )
            return data
        },
    })
}

function createDose(appliancePlanId:number, data: Dose, selectedDay: number) {
    return axios.get(`/api/dose_time_frame/create?appliance_plan_id=${encodeURIComponent(appliancePlanId)
        }&begins_at=${encodeURIComponent(data.begins_at)
        }&ends_at=${encodeURIComponent(data.ends_at)
        }&day_of_week=${encodeURIComponent(selectedDay)
        }&doses=${encodeURIComponent(
            JSON.stringify(data.dose_time_frame_dose_type_junctions.map(
                timeFrame => ({
                    count: timeFrame.doses_count, dose_type_id: timeFrame.dose_type_id
                })
            ))
        )}`
    )
}

function deleteDose(doseId: number) {
    return axios.get(`/api/dose_time_frame/delete?id=${encodeURIComponent(doseId)}`)
}

export function useDuplicateDosesToOtherDays(appliancePlanId, selectedDay: number) {
    const queryClient = useQueryClient()
    const doseAddUndo = useDoseAddUndo(appliancePlanId)

    return useMutation({
        mutationFn: async function () {
            const tasks: Promise<any>[] = []

            doseAddUndo.beginAdding();

            const doses = await axios.get<Dose[]>(
                '/api/dose_time_frame/list?appliance_plan_id=' + encodeURIComponent(appliancePlanId),
            )            
            const currentDayDoses = doses.data.filter(dose => dose.day_of_week == selectedDay)
            currentDayDoses.forEach(copiedDose => {
                for(let day = 0; day < 7; day++) { // for each day of week
                    if(day !== selectedDay) {
                        const task = createDose(appliancePlanId, copiedDose, day)

                        task.then((newDose) => {
                            doseAddUndo.addDose(newDose.data.id)
                        })

                        tasks.push(task)
                    }
                }
            })

            Promise.all(tasks).then(() => {
                doseAddUndo.endAdding()
            })

            return Promise.all(tasks)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['doses-' + appliancePlanId] })
        }
    })
}

export const useCreateDose = (estateId: number, appliancePlanId: number, selectedDay: 1|2|3|4|5|6|0, returnToList) => {
    const queryClient = useQueryClient()
    const doseAddUndo = useDoseAddUndo(appliancePlanId)

    return useMutation({
        mutationFn: (data: DoseFormInput) => {
            doseAddUndo.beginAdding()

            const beginsAt = data.begins_at.toISOString()
            const endsAt = data.ends_at.toISOString()
            let response = axios.get(`/api/dose_time_frame/create?appliance_plan_id=${encodeURIComponent(appliancePlanId)
                }&begins_at=${encodeURIComponent(beginsAt)
                }&ends_at=${encodeURIComponent(endsAt)
                }&day_of_week=${encodeURIComponent(selectedDay)
                }&doses=[{"count": ${encodeURIComponent(data.shower_count)
                }, "dose_type_id": 1}, {"count": ${encodeURIComponent(data.bath_count)
                }, "dose_type_id": 2}]`
            )

            response.then(newDose => {
                doseAddUndo.addDose(newDose.data.id)

                doseAddUndo.endAdding()
            })

            return response
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['doses-' + appliancePlanId] })
            queryClient.invalidateQueries({ queryKey: [ESTATE_IS_READY_QUERY_KEY(estateId)] })
            returnToList()
        },
    })
}

export const useUpdateDose = (doseId, appliancePlanId, resetDrawer) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: DoseFormInput) => {
            const beginsAt = data.begins_at.toISOString()
            const endsAt = data.ends_at.toISOString()
            return axios.get(`/api/dose_time_frame/update?id=${doseId
                }&appliance_plan_id=${appliancePlanId
                }&begins_at=${beginsAt
                }&ends_at=${endsAt
                }&doses=[{"count": ${data.shower_count
                }, "dose_type_id": 1}, {"count": ${data.bath_count
                }, "dose_type_id": 2}]`
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['doses-' + appliancePlanId] })
            queryClient.invalidateQueries({ queryKey: ['dose-' + doseId] })
            resetDrawer()
        },
    })
}

export const useDeleteDose = (estateId: number, doseId: number, appliancePlanId: number) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => {
            return axios.get(`/api/dose_time_frame/delete?id=${doseId}`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['doses-' + appliancePlanId] })
            queryClient.invalidateQueries({ queryKey: [ESTATE_IS_READY_QUERY_KEY(estateId)] })
        },
    })
}

export const useDeleteAllDoses = (estateId: number, appliancePlanId: number) => {
    const queryClient = useQueryClient()
    const doseAddUndo = useDoseAddUndo(appliancePlanId)

    return useMutation({
        mutationFn: async () => {
            const tasks: Promise<any>[] = []

            const doses = await axios.get<Dose[]>(
                '/api/dose_time_frame/list?appliance_plan_id=' + encodeURIComponent(appliancePlanId),
            )
            doses.data.forEach(dose => {
                tasks.push(deleteDose(dose.id))
            })

            const result = Promise.all(tasks)

            // can't add doses when they are cleared
            result.then(() => {
                doseAddUndo.clear()
            })

            return result
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['doses-' + appliancePlanId] })
            queryClient.invalidateQueries({ queryKey: [ESTATE_IS_READY_QUERY_KEY(estateId)] })
        },
    })
}

export const useDeleteDoses = (estateId: number, appliancePlanId: number) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (dosedIdArray: number[]) => {
            const tasks: Promise<any>[] = []

            dosedIdArray.forEach(doseId => {
                tasks.push(deleteDose(doseId))
            })

            return Promise.all(tasks)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['doses-' + appliancePlanId] })
            queryClient.invalidateQueries({ queryKey: [ESTATE_IS_READY_QUERY_KEY(estateId)] })
        },
    })
}

